import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';

interface LogEntry {
  log_id: number;
  timestamp: string;
  level: string;
  message: string;
  context?: object;
  stack_trace?: string;
}

interface LogsApiResponse {
  data: LogEntry[];
  page: number;
  limit: number;
  totalPages: number;
  totalLogs: number;
}

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>(''); // e.g., ERROR, INFO
  const logsPerPage = 25;

  const fetchLogs = useCallback(async (page: number, level?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params: { page: number; limit: number; level?: string } = {
        page,
        limit: logsPerPage,
      };
      if (level) {
        params.level = level.toUpperCase();
      }
      
      const response = await apiClient.get<LogsApiResponse>('/api/logs', { params });
      const data = response.data;
      
      setLogs(data.data || []);
      setCurrentPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
      setTotalLogs(data.totalLogs || 0);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch logs.';
      setError(errorMessage);
      setLogs([]); // Clear logs on error
    } finally {
      setIsLoading(false);
    }
  }, [logsPerPage]); // Include logsPerPage in dependencies

  useEffect(() => {
    fetchLogs(currentPage, levelFilter);
  }, [currentPage, levelFilter, fetchLogs]);

  const handleLevelFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLevelFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    // Simple pagination: Prev, Next, and current page
    // More complex pagination can be added if needed
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage > 1) {
      pages.push(<button key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={isLoading}>Precedente</button>);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => setCurrentPage(i)} disabled={isLoading || i === currentPage} style={i === currentPage ? { fontWeight: 'bold' } : {}}>
          {i}
        </button>
      );
    }

    if (currentPage < totalPages) {
      pages.push(<button key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={isLoading}>Successivo</button>);
    }
    return <div style={{ marginTop: '10px' }}>{pages}</div>;
  };

  return (
    <div style={{ margin: '20px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
      <h2>Application Log</h2>
      <div>
        <label htmlFor="levelFilter" style={{ marginRight: '10px' }}>Filtra per Livello:</label>
        <select id="levelFilter" value={levelFilter} onChange={handleLevelFilterChange} disabled={isLoading}>
          <option value="">Tutti</option>
          <option value="INFO">INFO</option>
          <option value="WARN">WARN</option>
          <option value="ERROR">ERROR</option>
          <option value="DEBUG">DEBUG</option>
        </select>
        <span style={{ marginLeft: '20px' }}>Totale Log: {totalLogs}</span>
      </div>

      {isLoading && <p>Caricamento log...</p>}
      {error && <p style={{ color: 'red' }}>Errore: {error}</p>}

      {!isLoading && !error && logs.length === 0 && <p>Nessun log trovato.</p>}

      {!isLoading && !error && logs.length > 0 && (
        <>
          <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>ID</th>
                <th style={tableHeaderStyle}>Timestamp</th>
                <th style={tableHeaderStyle}>Livello</th>
                <th style={tableHeaderStyle}>Messaggio</th>
                <th style={tableHeaderStyle}>Contesto</th>
                <th style={tableHeaderStyle}>Stack Trace</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.log_id}>
                  <td style={tableCellStyle}>{log.log_id}</td>
                  <td style={tableCellStyle}>{new Date(log.timestamp).toLocaleString()}</td>
                  <td style={tableCellStyle}>{log.level}</td>
                  <td style={tableCellStyle}>{log.message}</td>
                  <td style={tableCellStyle}>
                    {log.context ? <pre style={preStyle}>{JSON.stringify(log.context, null, 2)}</pre> : 'N/A'}
                  </td>
                  <td style={tableCellStyle}>
                    {log.stack_trace ? <pre style={preStyle}>{log.stack_trace}</pre> : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </>
      )}
    </div>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '8px',
  textAlign: 'left',
  backgroundColor: '#f2f2f2',
};

const tableCellStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '8px',
  textAlign: 'left',
  verticalAlign: 'top',
};

const preStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: '150px',
  overflowY: 'auto',
  backgroundColor: '#f9f9f9',
  padding: '5px',
  border: '1px solid #eee',
  fontSize: '0.9em',
};

export default LogViewer;
